import { useMemo, useState } from 'react';
import { AlertTriangle, Loader2, MapPin, Tags } from 'lucide-react';
import * as api from '../worlds.api';
import { toast } from '../worlds.store';
import { fmtInt } from '../constants';
import type { Country, GeoRegion } from '../worlds.types';
import { Modal } from './Modal';
import { ColorChip, TintBadge, cx } from './ui';
import styles from '../../LootboxPage.module.scss';
import ws from '../WorldsPage.module.scss';
import cs from './countries.module.scss';

// ============================================================
// Associer des pays sélectionnés à une géo-région (ou les en retirer), en un seul POST
// ============================================================
// La région proposée d'office est celle que tous les pays visés partagent déjà (sinon aucune).
// Pas de garde de région source ici : la sélection mélange des pays de régions différentes.
// ============================================================

export function BulkRegionModal({ countries, geoRegions, onClose, onDone, onGoRegions }: {
  /** Pays visés (sélection). */
  countries: Country[];
  geoRegions: GeoRegion[];
  onClose: () => void;
  /** Après succès : ids envoyés et région posée (null = retirés). */
  onDone: (ids: string[], geoRegionId: string | null) => void;
  /** Ouvre l'onglet Régions (aide « aucune région »). */
  onGoRegions?: () => void;
}) {
  const regionById = useMemo(() => new Map(geoRegions.map((r) => [r.id, r])), [geoRegions]);
  const [target, setTarget] = useState(() => {
    const set = new Set(countries.map((c) => c.geoRegionId ?? ''));
    const only = set.size === 1 ? [...set][0] : '';
    return regionById.has(only) ? only : '';
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Répartition actuelle des pays visés : « Asie (2) · aucune (1) ».
  const spread = useMemo(() => {
    const m = new Map<string, number>();
    countries.forEach((c) => m.set(c.geoRegionId ?? '', (m.get(c.geoRegionId ?? '') ?? 0) + 1));
    return [...m.entries()].sort((a, b) => b[1] - a[1]);
  }, [countries]);

  const region = target ? regionById.get(target) : undefined;
  const already = countries.filter((c) => (c.geoRegionId ?? '') === target).length;
  const willChange = countries.length - already;

  const apply = async () => {
    if (busy || countries.length === 0) return;
    setBusy(true);
    setError(null);
    const ids = countries.map((c) => c.id);
    const geoRegionId = target || null;
    const res = await api.assignGeoRegion(ids, geoRegionId);
    setBusy(false);
    if (!res.data) { setError(res.error ?? 'Affectation en échec'); return; }
    const n = res.data.updated;
    const rest = ids.length - n;
    toast.success(`${fmtInt(n)} pays ${geoRegionId ? `associé(s) à ${region?.name ?? geoRegionId}` : 'retiré(s) de leur région'}`
      + (rest > 0 ? ` (${fmtInt(rest)} déjà dans cet état)` : ''));
    onDone(ids, geoRegionId);
    onClose();
  };

  return (
    <Modal size="sm" busy={busy} onClose={onClose} onSubmit={() => void apply()}
      icon={<Tags size={16} aria-hidden />}
      title={`Associer ${fmtInt(countries.length)} pays à une région`}
      subtitle="Un seul envoi pour toute la sélection. Les chunks et le roster ne sont pas touchés."
      footer={(
        <>
          {error && <span className={ws.footError}><AlertTriangle size={13} aria-hidden /> {error}</span>}
          <button type="button" className={styles.secondaryBtn} onClick={onClose} disabled={busy}>Annuler</button>
          <button type="submit" className={styles.primaryBtn} disabled={busy || willChange === 0}
            title={willChange === 0 ? 'Tous les pays visés sont déjà dans cet état' : undefined}>
            {busy ? <Loader2 size={14} className={ws.spinning} aria-hidden /> : <Tags size={14} aria-hidden />}
            {target ? 'Associer' : 'Retirer de leur région'}
          </button>
        </>
      )}>
      <div className={cs.fieldBlock}>
        <span className={styles.label}>Pays visés ({fmtInt(countries.length)})</span>
        <div className={cs.chipList}>
          {countries.map((c) => (
            <span key={c.id} className={ws.chip} title={c.slugEn && c.slugEn !== c.slug ? `EN : ${c.slugEn}` : undefined}>
              <ColorChip color={c.color} size={10} />
              <span className={ws.mono}>{c.slug}</span>
            </span>
          ))}
        </div>
        <div className={cs.spread}>
          Régions actuelles :
          {spread.map(([id, n]) => {
            const r = id ? regionById.get(id) : undefined;
            return r
              ? <TintBadge key={id} color={r.color}>{r.name} ({fmtInt(n)})</TintBadge>
              : <span key={id || 'none'} className={ws.chip}>{id ? 'région inconnue' : 'aucune'} ({fmtInt(n)})</span>;
          })}
        </div>
      </div>

      <div className={styles.field} style={{ minWidth: 0 }}>
        <label className={styles.label} htmlFor="worlds-bulk-region">Géo-région</label>
        <span className={cs.withChip}>
          {region ? <ColorChip color={region.color} size={16} title={region.color} /> : null}
          <select id="worlds-bulk-region" className={styles.input} value={target} disabled={busy} autoFocus onChange={(e) => setTarget(e.target.value)}>
            <option value="">Aucune : retirer la région actuelle</option>
            {geoRegions.map((r) => <option key={r.id} value={r.id}>{r.name} ({r.slug})</option>)}
          </select>
        </span>
        {geoRegions.length === 0 ? (
          <span className={ws.fieldHint}>
            Aucune région pour l'instant.{' '}
            {onGoRegions && (
              <button type="button" className={cx(ws.linkBtn, ws.inlineIcon)} onClick={onGoRegions}>
                <MapPin size={12} aria-hidden /> Créer une région dans l'onglet Régions
              </button>
            )}
          </span>
        ) : (
          <span className={ws.fieldHint}>
            {willChange === 0
              ? 'Tous les pays visés sont déjà dans cet état : rien à changer.'
              : `${fmtInt(willChange)} pays changeront de région${already ? `, ${fmtInt(already)} y sont déjà` : ''}.`}
          </span>
        )}
      </div>
    </Modal>
  );
}
